import React from 'react';
import { ui } from '../../../config/uiSizes';
import { getTypeLabelByApiName } from '../../../utils/resourceTypes';
import { computeNamePreview, isNameTransform } from '../../../utils/transformationPreview';

interface TransformationNamePreviewProps {
    /** The transformation's type value, e.g. "AddPrefix". */
    transformationType: string;
    /** The transformation's parameters (prefix / mappings). */
    parameters?: Record<string, unknown>;
    /** Selected resource-type display names to preview. */
    resourceTypes: string[];
    /** Map of resource-type display name -> resource names, from the export. */
    resourcesByType: Record<string, string[]>;
}

/**
 * Pre-commit preview of the name changes a name-scoping transformation
 * (AddPrefix / RemovePrefix / RenameResource) will make. Renders a compact
 * before -> after sample per selected resource type so the user can confirm the
 * effect before approving the transform job. Renders nothing when the
 * transformation does not rename resources or when there is nothing to preview.
 */
const TransformationNamePreview: React.FC<TransformationNamePreviewProps> = ({
    transformationType,
    parameters,
    resourceTypes,
    resourcesByType
}) => {
    if (!isNameTransform(transformationType)) return null;
    if (resourceTypes.length === 0) return null;

    const groups = computeNamePreview(transformationType, parameters, resourceTypes, resourcesByType);
    if (groups.length === 0) return null;

    const totalChanged = groups.reduce((sum, g) => sum + g.changedCount, 0);

    return (
        <div className={`border-2 border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20 ${ui.page.cardPadding}`}>
            <div className={`flex items-center ${ui.gap.sm} ${ui.mb.title}`}>
                <svg
                    className={`${ui.svg.sm} text-blue-600 dark:text-blue-400 flex-shrink-0`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                </svg>
                <h4 className={`${ui.text.sectionTitle} font-bold text-blue-900 dark:text-blue-100`}>
                    Preview of name changes
                </h4>
            </div>

            <p className={`${ui.text.xs} text-blue-800 dark:text-blue-200 ${ui.mb.sm}`}>
                {totalChanged > 0
                    ? `${totalChanged} resource name${totalChanged === 1 ? '' : 's'} will change. Showing a sample below.`
                    : 'No resource names would change with the current settings.'}
            </p>

            <div className={`flex flex-col ${ui.gap.md}`}>
                {groups.map((group) => (
                    <div key={group.resourceType}>
                        <div className={`flex items-center ${ui.gap.sm} ${ui.mb.sm}`}>
                            <span className={`${ui.text.label} font-semibold text-blue-900 dark:text-blue-100`}>
                                {getTypeLabelByApiName(group.resourceType)}
                            </span>
                            <span className={`${ui.badge.sm} rounded-full bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 font-medium`}>
                                {group.changedCount} of {group.totalCount} changing
                            </span>
                        </div>
                        <ul className={`flex flex-col ${ui.gap.xs}`}>
                            {group.rows.map((row, idx) => (
                                <li
                                    key={`${group.resourceType}-${idx}`}
                                    className={`flex items-center ${ui.gap.sm} ${ui.text.xs}`}
                                >
                                    <span className="text-gray-600 dark:text-gray-400 line-through break-all">
                                        {row.before}
                                    </span>
                                    <svg
                                        className={`${ui.svg.sm} text-blue-500 dark:text-blue-400 flex-shrink-0`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                                        />
                                    </svg>
                                    <span
                                        className={`break-all font-medium ${row.changed
                                                ? 'text-emerald-700 dark:text-emerald-400'
                                                : 'text-gray-500 dark:text-gray-400'
                                            }`}
                                    >
                                        {row.after}
                                    </span>
                                    {!row.changed && (
                                        <span className={`${ui.text.xxs} text-gray-400 dark:text-gray-500 italic`}>
                                            (no change)
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TransformationNamePreview;
